// File: client/src/components/PostCard.js

import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DOMPurify from "dompurify";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Comments from "./Comments";
import useInViewTime from "../hooks/useInViewTime";
import FollowButton from "./FollowButton";
import { fetchPostById } from "../api";

import { Link } from "react-router-dom";
import {
  addComment,
  toggleLikePost,
  togglePostVisibility,
  trackViewTime,
  getPostMetrics,
} from "../api";
import { fetchComponentCode } from "../api";
import DynamicComponent from "./DynamicComponent";
import { rewriteToCloudFront } from "../images";
import { getOrCreateUserId } from "../api";
import ProfileAvatar from "./ProfileAvatar";
import { formatDistanceToNow } from "date-fns";
import { postBodyStyles } from "../styles/postBody";

const PostCard = React.memo(function PostCard({
  post,
  onPostUpdated,
  isShared,
  isAdmin,
}) {
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showFullText, setShowFullText] = useState(false);
  const [source, setSource] = useState(null);
  /* NEW: when a dynamic component is broken or stalls, hide this post for non-admins */
  const [hideBecauseDynamicBroken, setHideBecauseDynamicBroken] =
    useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showShareNotification, setShowShareNotification] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const contentRef = useRef(null);
  const userId = getOrCreateUserId();

  /* --------------------------------------------------------------
   * If post.text looks like JSON but fails to parse, treat it as
   * invalid JSON and skip the whole post for non-admin users.
   * Admins will see a small failure notice instead of the body.
   * -------------------------------------------------------------- */
  const rawText = (post.text || "").trim();
  const looksLikeJson =
    rawText.startsWith("{") ||
    rawText.startsWith("[") ||
    rawText.startsWith("(empty)");
  let invalidJson = false;
  if (looksLikeJson) {
    try {
      JSON.parse(rawText);
    } catch {
      invalidJson = true;
    }
  }

  /* --------------------------------------------------------------
   * Track whether the card itself is currently on screen.
   * -------------------------------------------------------------- */
  const [inView, setInView] = useState(false);

  // Hold the latest callback in a ref to avoid effect re-runs
  const onPostUpdatedRef = useRef(onPostUpdated);
  useEffect(() => {
    onPostUpdatedRef.current = onPostUpdated;
  });

  // View tracking
  const handleExitView = async (timeInSeconds) => {
    // Don't track very short views (less than 0.5 seconds)
    if (timeInSeconds < 0.5) return;

    try {
      await trackViewTime(post.id, timeInSeconds);
    } catch (err) {
      console.error("Failed to track view time:", err);
    }
  };

  const observeRef = useInViewTime({
    onEnterView: () => setInView(true),
    onExitView: (t) => {
      // t = seconds spent in view
      setInView(false); // reset flag
      handleExitView(t); // keep your original logic
    },
  });

  // Fetch metrics if admin
  useEffect(() => {
    if (isAdmin) {
      const adminKey = localStorage.getItem("adminKey");
      if (!adminKey) return;

      const fetchMetrics = async () => {
        try {
          const data = await getPostMetrics(post.id, adminKey);
          setMetrics(data);
        } catch (err) {
          console.error("Error fetching post metrics:", err);
        }
      };

      fetchMetrics();
    }
  }, [isAdmin, post.id]);

  // ─────────────────────────────────────────────────────────────
  // Remove anything wrapped in <hidden>…</hidden> **before**
  // sanitation so secrets/spoilers never reach the DOM.
  // ─────────────────────────────────────────────────────────────
  const cleanText = (post.text || "").replace(
    /<hidden[^>]*>[\s\S]*?<\/hidden>/gi,
    ""
  );

  // Now sanitize the remaining HTML as before
  const safeHtml = DOMPurify.sanitize(cleanText, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "i",
      "b",
      "u",
      "a",
      "code",
      "pre",
      "blockquote",
      "ul",
      "ol",
      "li",
      "img",
      "h1",
      "h2",
      "h3",
      "h4",
      "span",
      "div",
      "sup",
      "sub",
      "mark",
      "small",
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "title",
      "target",
      "style", // We'll allow style but with restrictions
    ],
    ALLOW_DATA_ATTR: false,
    SANITIZE_DOM: true,
    // NOTE: we only use the sanitized HTML when !invalidJson
    WHOLE_DOCUMENT: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
    RETURN_TRUSTED_TYPE: false,
    FORBID_TAGS: [
      "script",
      "style",
      "form",
      "input",
      "button",
      "textarea",
      "select",
      "option",
      "meta",
      "link",
      "base",
      "object",
      "embed",
      "param",
      "noscript",
      "iframe",
    ],
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onmouseenter",
      "onmouseleave",
      "onblur",
      "onfocus",
      "onchange",
      "onsubmit",
      "onreset",
      "onkeydown",
      "onkeypress",
      "onkeyup",
    ],
  });

  // Additional style sanitization for allowed CSS properties
  const sanitizeStyle = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;

    const allowedStyles = [
      "color",
      "background-color",
      "font-family",
      "font-size",
      "font-weight",
      "font-style",
      "text-decoration",
      "text-align",
      "line-height",
      "letter-spacing",
      "text-transform",
      "text-shadow",
      "opacity",
      "margin",
      "padding",
    ];

    const sanitizeElement = (element) => {
      if (element.style) {
        const style = element.getAttribute("style");
        if (style) {
          const styles = style.split(";").filter((s) => s.trim());
          const safeStyles = styles.filter((s) => {
            const [property] = s.split(":").map((p) => p.trim().toLowerCase());
            return allowedStyles.includes(property);
          });
          if (safeStyles.length > 0) {
            element.setAttribute("style", safeStyles.join(";"));
          } else {
            element.removeAttribute("style");
          }
        }
      }
      Array.from(element.children).forEach(sanitizeElement);
    };

    sanitizeElement(div);
    return div.innerHTML;
  };

  // Combine both sanitizations
  const fullySafeHtml = sanitizeStyle(safeHtml);

  // ───────── cross-browser overflow detection (mobile & desktop) ─────────
  /**
   * iOS Safari's `getBoundingClientRect()` ignores `-webkit-line-clamp`,
   * so we switch to a simpler, reliable test: if the element can scroll
   * vertically, some lines are hidden and we need a "Show more" button.
   */
  const detectOverflow = () => {
    if (!contentRef.current || showFullText) return;

    const el = contentRef.current;
    setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
  };

  /* initial run + whenever HTML/text changes */
  useLayoutEffect(detectOverflow, [fullySafeHtml, showFullText]);

  /* react to viewport changes (rotation / resize) */
  useEffect(() => {
    window.addEventListener("resize", detectOverflow, { passive: true });
    window.addEventListener("orientationchange", detectOverflow, {
      passive: true,
    });
    return () => {
      window.removeEventListener("resize", detectOverflow);
      window.removeEventListener("orientationchange", detectOverflow);
    };
  }, [showFullText]);

  /* react to late size changes inside the post (e.g. images) */
  useEffect(() => {
    if (!contentRef.current || showFullText) return;
    const ro = new ResizeObserver(detectOverflow);
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [showFullText]);

  // If we have a componentId, fetch its code
  useEffect(() => {
    if (!post.componentId) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchComponentCode(post.componentId);
        if (!cancelled) setSource(data.source);
      } catch (err) {
        console.error("Error loading component code:", err);
        if (!isAdmin) setHideBecauseDynamicBroken(true); // hide for public
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [post.componentId, isAdmin]);

  // Add a short fetch watchdog (optional but useful)
  // If the code hasn't arrived in ~4.5s, hide it for non-admins
  useEffect(() => {
    if (!post.componentId || isAdmin) return;
    if (source) return; // arrived in time
    const id = setTimeout(() => {
      if (!source) setHideBecauseDynamicBroken(true);
    }, 500);
    return () => clearTimeout(id);
  }, [post.componentId, source, isAdmin]);

  // Toggle comments
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Toggle read-more for text
  const toggleShowFullText = () => {
    setShowFullText(!showFullText);
  };

  // Add a new comment
  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;
    try {
      const updatedPost = await addComment(post.id, commentText);
      onPostUpdated(updatedPost);
      setCommentText("");
    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  // ---- NEW: handle toggling like/unlike
  const handleLikeToggle = async () => {
    try {
      const { post: updated } = await toggleLikePost(post.id);

      // The server already gave us updated.userHasLiked and updated.likeCount
      const newPost = {
        ...post,
        userHasLiked: updated.userHasLiked,
        likeCount: updated.likeCount,
        comments: updated.comments,
        // copy anything else you need
      };

      onPostUpdated(newPost);
    } catch (err) {
      console.error("Failed toggling like:", err);
      alert("Could not toggle like");
    }
  };

  // For the share link
  const handleShare = () => {
    const domain = window.location.origin;
    const shareUrl = `${domain}/#/?share=${post.id}`;

    // Create a temporary input element
    const tempInput = document.createElement("input");
    tempInput.value = shareUrl;
    document.body.appendChild(tempInput);

    // Try to use the clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setShowShareNotification(true);
          setTimeout(() => setShowShareNotification(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy link:", err);
          fallbackCopyToClipboard();
        });
    } else {
      // Fallback for browsers where clipboard API is not available
      fallbackCopyToClipboard();
    }

    function fallbackCopyToClipboard() {
      try {
        // Select the text
        tempInput.select();
        tempInput.setSelectionRange(0, 99999); // For mobile devices

        // Try to copy using document.execCommand
        const success = document.execCommand("copy");
        if (success) {
          setShowShareNotification(true);
          setTimeout(() => setShowShareNotification(false), 2000);
        } else {
          console.error("Failed to copy link using fallback method");
        }
      } catch (err) {
        console.error("Failed to copy link:", err);
      } finally {
        // Clean up
        document.body.removeChild(tempInput);
      }
    }
  };

  const handleVisibilityToggle = async () => {
    try {
      const updatedPost = await togglePostVisibility(post.id);
      onPostUpdated(updatedPost);
    } catch (err) {
      console.error("Error toggling post visibility:", err);
    }
  };

  // ------------------------------------------------------------------
  // When the comment pane is open *and* the card is in the viewport
  // poll the server every 5 s and merge any new comments.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!expanded || !inView) return; // also skip when off-screen

    let active = true; // guard against async leak

    const poll = async () => {
      try {
        const fresh = await fetchPostById(post.id);
        if (active) onPostUpdatedRef.current(fresh); // always latest
      } catch (e) {
        console.error("Polling comments failed:", e);
      }
    };

    poll(); // grab once immediately
    const id = setInterval(poll, 5000); // then every 5 s
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [expanded, inView, post.id]); // no more churn

  /* Early skip for non-admins: invalid JSON payloads are hidden entirely */
  if (looksLikeJson && invalidJson && !isAdmin) {
    return null;
  }

  // If the dynamic part is broken/stuck, hide entire card for non-admins
  if (post.componentId && hideBecauseDynamicBroken && !isAdmin) {
    return null;
  }

  return (
    <>
      <Card
        ref={observeRef}
        className="post-card"
        sx={{
          mb: 0,
          backgroundColor: isShared
            ? "rgba(29, 155, 240, 0.05)"
            : "transparent",
          borderRadius: 0,
          borderBottom: 1,
          borderColor: "divider",
          position: "relative",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: isShared
              ? "rgba(29, 155, 240, 0.08)"
              : "background.hover",
          },
          mx: 0,
          width: "100%",
          ...(isShared && {
            borderLeft: "4px solid",
            borderLeftColor: "primary.main",
          }),
        }}
      >
        <CardContent
          sx={{
            p: 2,
            "&:last-child": { pb: 2 },
            px: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1,
            }}
          >
            {/* avatar */}
            <ProfileAvatar profile={post.AIProfile} size={40} />

            {/* name + time block */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                  fontSize: "0.938rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {post.AIProfile ? (
                  <Link
                    to={`/ai/${post.AIProfile.id}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {post.AIProfile.name}
                  </Link>
                ) : (
                  "User Name"
                )}
              </Typography>

              {/* put the timestamp on its own line for xs */}
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.813rem",
                  mt: 0.25,
                  display: { xs: "block", sm: "inline" },
                }}
              >
                {post.createdAt &&
                  formatDistanceToNow(new Date(post.createdAt), {
                    addSuffix: true,
                  })}
              </Typography>
            </Box>

            {/* follow button – never shrinks, always far right */}
            <FollowButton profileId={post.AIProfile?.id} small />

            {isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={post.isVisible}
                    onChange={handleVisibilityToggle}
                    size="small"
                  />
                }
                label="Visible"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          {/* Post text with clamp (only if JSON is valid or not JSON at all) */}
          {!invalidJson && (
            <Box
              ref={contentRef}
              sx={{
                ...postBodyStyles,
                mb: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: showFullText ? "unset" : 8,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                position: "relative",
                isolation: "isolate",
              }}
              dangerouslySetInnerHTML={{ __html: fullySafeHtml }}
            />
          )}

          {/* Admin-only failure notice when body looked like JSON but was invalid */}
          {invalidJson && isAdmin && (
            <Box
              sx={{
                p: 1.5,
                mb: 1.5,
                border: 1,
                borderColor: "error.main",
                borderRadius: 1,
                backgroundColor: "rgba(244,67,54,0.1)",
              }}
            >
              <Typography variant="body2" sx={{ color: "error.main" }}>
                Post body looks like JSON but is invalid. Hidden from users.
              </Typography>
            </Box>
          )}

          {/* "Show more"/"Show less" */}
          {!showFullText && isOverflowing && (
            <Button
              onClick={toggleShowFullText}
              sx={{
                color: "primary.main",
                p: 0,
                minHeight: 0,
                mb: 1.5,
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
            >
              Show more
            </Button>
          )}
          {showFullText && (
            <Button
              onClick={toggleShowFullText}
              sx={{
                color: "primary.main",
                p: 0,
                minHeight: 0,
                mb: 1.5,
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
            >
              Show less
            </Button>
          )}

          {/* If there's an image: */}
          {post.imageUrl && (
            <Box
              sx={{
                mb: 1.5,
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
              }}
            >
              <Box
                component="img"
                src={rewriteToCloudFront(post.imageUrl)}
                alt="Post media"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
          )}

          {/* If there's a dynamic component (skip when invalid JSON for admin view too): */}
          {post.componentId && source && !hideBecauseDynamicBroken && (
            <DynamicComponent
              source={source}
              props={{}}
              maxWaitMs={3500}
              isAdmin={isAdmin}
              onFail={(reason) => {
                console.warn(
                  "Dynamic component failed for post",
                  post.id,
                  reason
                );
                if (!isAdmin) setHideBecauseDynamicBroken(true); // hide entire post
              }}
            />
          )}

          {/* Interaction buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              maxWidth: "100%",
              mx: -1,
            }}
          >
            {/* Comment */}
            <Button
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={toggleExpand}
              sx={{
                flex: 1,
                color: "text.secondary",
                minWidth: 0,
                px: 1,
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "rgba(29, 155, 240, 0.1)",
                },
              }}
            >
              {post.comments?.length || 0}
            </Button>

            {/* Like/Unlike */}
            <Button
              startIcon={
                post.userHasLiked ? (
                  <ThumbUpIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ThumbUpOffAltIcon sx={{ fontSize: 18 }} />
                )
              }
              onClick={handleLikeToggle}
              sx={{
                flex: 1,
                color: post.userHasLiked ? "primary.main" : "text.secondary",
                minWidth: 0,
                px: 1,
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "rgba(29, 155, 240, 0.1)",
                },
              }}
            >
              {post.likeCount || 0}
            </Button>

            {/* Share */}
            <Button
              startIcon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
              onClick={handleShare}
              sx={{
                flex: 1,
                color: "text.secondary",
                minWidth: 0,
                px: 1,
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "rgba(29, 155, 240, 0.1)",
                },
              }}
            >
              Share
            </Button>
          </Box>

          {/* Comments Section */}
          {expanded && (
            <Comments
              comments={post.comments?.map((comment) => ({
                id: comment.id,
                text: comment.text,
                author: comment.author,
                userInfo: comment.userInfo,
                createdAt: comment.createdAt || new Date().toISOString(),
              }))}
              onSubmitComment={async (commentData) => {
                try {
                  const updatedPost = await addComment(
                    post.id,
                    commentData.text,
                    commentData.author,
                    commentData.userInfo
                  );
                  onPostUpdated(updatedPost);
                } catch (error) {
                  console.error("Failed to add comment:", error);
                }
              }}
            />
          )}

          {isAdmin && metrics && (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              <span>{metrics.uniqueViewers} viewers</span>
              <span>•</span>
              <span>{Math.round(metrics.totalTime)}s total time</span>
              {metrics.lastViewedAt && (
                <>
                  <span>•</span>
                  <span>
                    Last: {new Date(metrics.lastViewedAt).toLocaleString()}
                  </span>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={showShareNotification}
        autoHideDuration={2000}
        onClose={() => setShowShareNotification(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowShareNotification(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
});

export default PostCard;
